import { observer } from 'mobx-react-lite';
import React, { useState, useEffect } from 'react';
import { FormattedDate } from 'react-intl';
import { Link, useRouteMatch } from 'react-router-dom';
import Box from 'ui-box';
import {
  Button,
  Container,
  defaultColors,
  defaultFills,
  Heading,
  Icon,
  KeyValue,
  majorScale,
  Menu,
  MenuDivider,
  MenuItem,
  minorScale,
  PageContainer,
  PageHeader,
  PageLoader,
  Popover,
  Section,
  Spinner,
  Tag,
} from '../../npaper';
import { IAgreement, useStores } from '../../stores';
import { CompanyThumbnail } from '../companies';
import { ContactThumbnail } from '../contacts';
import { SaleItemsTable } from '../sales/SaleItemsTable';
import { UserThumbnail } from '../settings/UserThumbnail';
import { AgreementDetailsDialog } from './AgreementDetailsDialog';
import { AgreementStatusTag } from './AgreementStatusTag';

export const AgreementPage = observer(() => {
  const match = useRouteMatch<{ id: string }>();
  const id = match.params.id;

  const [agreement, setAgreement] = useState<IAgreement>();
  const [notFound, setNotFound] = useState(false);

  const { agreementStore } = useStores();

  useEffect(() => {
    let isSub = true;
    setAgreement(undefined);
    agreementStore
      .getItem(id, true)
      .then((sale) => {
        isSub && setAgreement(sale);
      })
      .catch(() => setNotFound(true));
    return () => {
      isSub = false;
    };
  }, [agreementStore, id]);

  const [showDetails, setShowDetails] = useState(false);

  return !agreement ? (
    <PageContainer>
      {notFound ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <Icon
            icon="error"
            size={40}
            marginBottom={minorScale(2)}
            color={defaultFills.danger[500]}
          />
          <Heading size={400}>Kunne ikke finde aftalen</Heading>
          <p className="muted font-mono">ID {id}</p>
        </Box>
      ) : (
        <PageLoader />
      )}
    </PageContainer>
  ) : (
    <PageContainer
      position="relative"
      marginBottom={majorScale(10)}
      height="unset"
      maxHeight="unset"
    >
      <AgreementDetailsDialog
        isShown={showDetails}
        onClose={() => setShowDetails(false)}
        agreement={agreement}
      />
      <Container>
        <PageHeader
          title={
            <Box marginTop={majorScale(1)}>
              <Heading size={300} className="muted">
                Med{' '}
                {agreement.customer ? (
                  <Link
                    className="a"
                    to={`/companies/${agreement.customer.id}`}
                  >
                    {agreement.customer.name}
                  </Link>
                ) : (
                  <Spinner size={16} />
                )}
                <AgreementStatusTag
                  agreement={agreement}
                  marginLeft={majorScale(1)}
                  className="p"
                />
              </Heading>
              <Heading size={600}>{agreement.title}</Heading>
            </Box>
          }
          id={id}
          typeIcon="repeat"
          type="Løbende aftale"
          actions={
            <Popover
              placement="bottom-end"
              content={(close) => (
                <Menu>
                  <MenuDivider text="Ændringer" />
                  <MenuItem
                    text="Rediger detaljer"
                    icon="edit"
                    onClick={() => {
                      close();
                      setShowDetails(true);
                    }}
                  />
                  <MenuItem text="Dupliker" icon="duplicate" />
                  <MenuDivider />
                  <MenuItem
                    text="Opsig aftale"
                    intent="danger"
                    icon="remove"
                    onClick={() => close()}
                  />
                </Menu>
              )}
            >
              <Button iconRight="chevron-down">Handlinger</Button>
            </Popover>
          }
        />

        <Section
          title="Details"
          actions={
            <Button
              iconRight="edit"
              minimal={true}
              onClick={() => setShowDetails(true)}
            >
              Rediger
            </Button>
          }
        >
          <Box display="flex">
            <Box width="50%">
              <KeyValue
                keyName="Kunde"
                value={
                  agreement.customer ? (
                    <CompanyThumbnail company={agreement.customer} />
                  ) : (
                    <Spinner size={16} />
                  )
                }
              />
              <KeyValue keyName="Titel" value={agreement.title} />
              <KeyValue
                keyName="Kontaktpersoner"
                value={
                  agreement.contacts.length > 0 ? (
                    <>
                      {agreement.contacts.reduce((p, n) => p && !!n, true) ? (
                        agreement.contacts.map((contact) => (
                          <ContactThumbnail
                            key={contact.id}
                            marginBottom={majorScale(1)}
                            marginRight={majorScale(1)}
                            contact={contact}
                          />
                        ))
                      ) : (
                        <Spinner size={16} />
                      )}
                    </>
                  ) : undefined
                }
              />
              <KeyValue keyName="Valuta" value={agreement.currency} />
              <KeyValue
                keyName="Faktureringsadresse"
                value={
                  agreement.invoice_address
                    ? agreement.invoice_address.description
                    : undefined
                }
              />
              <KeyValue
                keyName="Leveringsadresse"
                placeholder="Samme som fakturering"
                value={
                  agreement.delivery_address
                    ? agreement.delivery_address.description
                    : undefined
                }
              />
              <KeyValue
                keyName="Start dato"
                value={
                  <FormattedDate format="default" value={agreement.startDate} />
                }
              />
              <KeyValue
                keyName="Slut dato"
                value={
                  agreement.endDate ? (
                    <FormattedDate format="default" value={agreement.endDate} />
                  ) : undefined
                }
              />
              <KeyValue keyName="Interval" value={agreement.intervalTitle} />
            </Box>
            <Box
              width="50%"
              paddingLeft="16px"
              borderLeft={`2px solid ${defaultColors.gray[100]}`}
              alignSelf="flex-start"
            >
              <KeyValue keyName="Aftalenummer" value={id} mono={true} />
              <KeyValue keyName="Reference" value={agreement.reference} />
              <KeyValue keyName="Beskrivelse" value={agreement.description} />
              <KeyValue
                keyName="Ansvarlige"
                value={
                  agreement.responsible.length > 0 ? (
                    agreement.responsible.reduce((p, n) => p && !!n, true) ? (
                      agreement.responsible.map((member, i) => (
                        <UserThumbnail
                          user={member!.user}
                          key={member!.id}
                          showName
                          marginRight={majorScale(2)}
                        />
                      ))
                    ) : (
                      <Spinner size={16} />
                    )
                  ) : undefined
                }
              />
              <KeyValue
                keyName="Tags"
                value={
                  agreement.tags.length > 0
                    ? agreement.tags.map((t) => (
                        <Tag
                          key={t}
                          small={true}
                          marginRight={minorScale(1)}
                          intent="info"
                        >
                          {t}
                        </Tag>
                      ))
                    : undefined
                }
              />
            </Box>
          </Box>
        </Section>

        <Section title="Linjer">
          <SaleItemsTable agreement={agreement} showTotals />
        </Section>

        {/*TODO: <Section title="Tidslinje">
          <TimeLine />
        </Section>*/}
      </Container>
    </PageContainer>
  );
});
